/**
 * Enhanced skill matching utility for VetriaAI platform
 * Matches jobseeker skills to job descriptions and requirements
 */

interface JobSkill {
  name: string;
  required: boolean;
  level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  weight?: number; // 1-10 scale, higher means more important
}

interface JobseekerSkill {
  name: string;
  verified: boolean;
  score?: number; // 0-100 verification score
  experience?: string; // years of experience
}

interface SkillMatchResult {
  matchScore: number; // 0-100 overall match percentage
  matchedSkills: string[];
  missingCriticalSkills: string[];
  skillBreakdown: {
    skill: string;
    matched: boolean;
    verified: boolean;
    importance: 'critical' | 'important' | 'nice-to-have';
    score?: number;
  }[];
  recommendations: string[];
}

/**
 * Normalize skill names for better matching
 */
function normalizeSkill(skill: string): string {
  return skill
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, ' ')
    .replace(/\b(js|javascript)\b/g, 'javascript')
    .replace(/\b(ts|typescript)\b/g, 'typescript')
    .replace(/\b(react\.?js|reactjs)\b/g, 'react')
    .replace(/\b(vue\.?js|vuejs)\b/g, 'vue')
    .replace(/\b(angular\.?js|angularjs)\b/g, 'angular')
    .replace(/\b(node\.?js|nodejs)\b/g, 'node')
    .replace(/\b(express\.?js|expressjs)\b/g, 'express')
    .replace(/\b(next\.?js|nextjs)\b/g, 'next')
    .replace(/\b(mongo\.?db|mongodb)\b/g, 'mongodb')
    .replace(/\b(postgres|postgresql|psql)\b/g, 'postgresql')
    .replace(/\b(mysql|my sql)\b/g, 'mysql')
    .replace(/\b(sql server|sqlserver|mssql)\b/g, 'sql server')
    .replace(/\b(aws|amazon web services)\b/g, 'aws')
    .replace(/\b(gcp|google cloud platform|google cloud)\b/g, 'gcp')
    .replace(/\b(azure|microsoft azure)\b/g, 'azure')
    .replace(/\b(k8s|kubernetes)\b/g, 'kubernetes')
    .replace(/\b(ci\/cd|cicd|continuous integration)\b/g, 'ci/cd')
    .replace(/\b(git|github|gitlab)\b/g, 'git')
    .replace(/\b(rest api|restful api|rest)\b/g, 'rest api')
    .replace(/\b(graphql|graph ql)\b/g, 'graphql')
    .replace(/\b(html5?|html)\b/g, 'html')
    .replace(/\b(css3?|css)\b/g, 'css')
    .replace(/\b(sass|scss)\b/g, 'sass')
    .replace(/\b(less css|less)\b/g, 'less')
    .replace(/\b(bootstrap|bs)\b/g, 'bootstrap')
    .replace(/\b(tailwind css|tailwindcss)\b/g, 'tailwind')
    .replace(/\b(material ui|mui|material-ui)\b/g, 'material ui')
    .replace(/\b(jest|unit testing|testing)\b/g, 'testing')
    .replace(/\b(cypress|e2e testing|end to end)\b/g, 'e2e testing')
    .replace(/\b(docker|containerization)\b/g, 'docker')
    .replace(/\b(redis|caching)\b/g, 'redis')
    .replace(/\b(elasticsearch|elastic search)\b/g, 'elasticsearch');
}

/**
 * Calculate similarity between two normalized skills
 */
function calculateSkillSimilarity(skill1: string, skill2: string): number {
  const s1 = normalizeSkill(skill1);
  const s2 = normalizeSkill(skill2);
  
  // Exact match
  if (s1 === s2) return 1.0;
  
  // Contains match
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;
  
  // Word overlap
  const words1 = s1.split(' ');
  const words2 = s2.split(' ');
  const commonWords = words1.filter(word => words2.includes(word));
  
  if (commonWords.length > 0) {
    return (commonWords.length / Math.max(words1.length, words2.length)) * 0.6;
  }
  
  // Levenshtein distance for fuzzy matching
  const distance = levenshteinDistance(s1, s2);
  const maxLen = Math.max(s1.length, s2.length);
  const similarity = 1 - (distance / maxLen);
  
  return similarity > 0.7 ? similarity * 0.5 : 0;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Extract skills from job description text
 */
export function extractSkillsFromJobDescription(jobDescription: string): JobSkill[] {
  const commonSkills = [
    'javascript', 'typescript', 'react', 'vue', 'angular', 'node', 'express',
    'python', 'django', 'flask', 'java', 'spring', 'c#', '.net', 'go',
    'rust', 'php', 'laravel', 'ruby', 'rails', 'html', 'css', 'sass',
    'tailwind', 'bootstrap', 'material ui', 'mongodb', 'postgresql', 'mysql',
    'redis', 'elasticsearch', 'aws', 'azure', 'gcp', 'docker', 'kubernetes',
    'jenkins', 'ci/cd', 'git', 'rest api', 'graphql', 'microservices',
    'testing', 'jest', 'cypress', 'selenium', 'agile', 'scrum', 'devops',
    'linux', 'ubuntu', 'nginx', 'apache', 'webpack', 'vite', 'babel',
    'eslint', 'prettier', 'figma', 'sketch', 'photoshop', 'ui/ux',
    'responsive design', 'accessibility', 'performance optimization',
    'security', 'authentication', 'oauth', 'jwt', 'encryption'
  ];
  
  const text = jobDescription.toLowerCase();
  const foundSkills: JobSkill[] = [];
  
  // Check for required skills (mentioned with "required", "must have", etc.)
  const requiredPatterns = [
    /required?\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /must\s+have\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /essential\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /mandatory\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi
  ];
  
  const niceToHavePatterns = [
    /nice\s+to\s+have\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /preferred\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /bonus\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi,
    /plus\s*:?\s*(.+?)(?:\.|;|,|\n|$)/gi
  ];
  
  // Find skills in common skills list
  commonSkills.forEach(skill => {
    const normalizedSkill = normalizeSkill(skill);
    const regex = new RegExp(`\\b${normalizedSkill.replace(/\s+/g, '\\s*')}\\b`, 'gi');
    
    if (regex.test(text)) {
      // Determine if it's required
      let isRequired = true;
      let weight = 5; // default weight
      
      // Check if mentioned in nice-to-have sections
      for (const pattern of niceToHavePatterns) {
        const matches = [...text.matchAll(pattern)];
        for (const match of matches) {
          if (match[1] && new RegExp(`\\b${normalizedSkill.replace(/\s+/g, '\\s*')}\\b`, 'i').test(match[1])) {
            isRequired = false;
            weight = 3;
            break;
          }
        }
      }
      
      // Check if mentioned in required sections (higher weight)
      for (const pattern of requiredPatterns) {
        const matches = [...text.matchAll(pattern)];
        for (const match of matches) {
          if (match[1] && new RegExp(`\\b${normalizedSkill.replace(/\s+/g, '\\s*')}\\b`, 'i').test(match[1])) {
            isRequired = true;
            weight = 8;
            break;
          }
        }
      }
      
      // Determine level based on context
      let level: 'beginner' | 'intermediate' | 'advanced' | 'expert' = 'intermediate';
      if (text.includes(`senior ${normalizedSkill}`) || text.includes(`expert ${normalizedSkill}`)) {
        level = 'expert';
        weight = Math.min(weight + 2, 10);
      } else if (text.includes(`junior ${normalizedSkill}`) || text.includes(`entry ${normalizedSkill}`)) {
        level = 'beginner';
      } else if (text.includes(`advanced ${normalizedSkill}`)) {
        level = 'advanced';
        weight = Math.min(weight + 1, 10);
      }
      
      foundSkills.push({
        name: skill,
        required: isRequired,
        level,
        weight
      });
    }
  });
  
  return foundSkills;
}

/**
 * Match jobseeker skills against job requirements
 */
export function matchSkillsToJob(
  jobseekerSkills: JobseekerSkill[],
  jobSkills: JobSkill[]
): SkillMatchResult {
  const skillBreakdown: SkillMatchResult['skillBreakdown'] = [];
  const matchedSkills: string[] = [];
  const missingCriticalSkills: string[] = [];
  const recommendations: string[] = [];
  
  let totalWeight = 0;
  let matchedWeight = 0;
  
  // Process each job skill requirement
  jobSkills.forEach(jobSkill => {
    totalWeight += jobSkill.weight || 5;
    
    let bestMatch: JobseekerSkill | null = null;
    let bestSimilarity = 0;
    
    // Find best matching skill from jobseeker
    jobseekerSkills.forEach(jsSkill => {
      const similarity = calculateSkillSimilarity(jsSkill.name, jobSkill.name);
      if (similarity > bestSimilarity && similarity > 0.5) { // Minimum threshold
        bestSimilarity = similarity;
        bestMatch = jsSkill;
      }
    });
    
    const importance = jobSkill.weight && jobSkill.weight >= 7 
      ? 'critical' as const
      : jobSkill.required 
        ? 'important' as const 
        : 'nice-to-have' as const;
    
    if (bestMatch) {
      matchedSkills.push(jobSkill.name);
      
      // Calculate weighted match score
      let skillScore = bestSimilarity * 100;
      
      // Bonus for verified skills
      if (bestMatch.verified && bestMatch.score) {
        skillScore = Math.min(skillScore * 1.2, 100); // 20% bonus for verification
      }
      
      // Penalty for level mismatch
      if (jobSkill.level === 'expert' && !bestMatch.verified) {
        skillScore *= 0.8; // 20% penalty for unverified expert-level skill
      }
      
      matchedWeight += (jobSkill.weight || 5) * (skillScore / 100);
      
      skillBreakdown.push({
        skill: jobSkill.name,
        matched: true,
        verified: bestMatch.verified,
        importance,
        score: Math.round(skillScore)
      });
    } else {
      skillBreakdown.push({
        skill: jobSkill.name,
        matched: false,
        verified: false,
        importance
      });
      
      if (importance === 'critical') {
        missingCriticalSkills.push(jobSkill.name);
        recommendations.push(`Consider learning ${jobSkill.name} - it's critical for this role`);
      } else if (importance === 'important') {
        recommendations.push(`${jobSkill.name} would strengthen your application`);
      }
    }
  });
  
  // Calculate overall match score
  const matchScore = totalWeight > 0 ? Math.round((matchedWeight / totalWeight) * 100) : 0;
  
  // Add general recommendations
  if (matchScore < 60) {
    recommendations.push('Consider taking our skills validation test to verify your abilities');
  }
  
  if (missingCriticalSkills.length > 0) {
    recommendations.push('Focus on developing the missing critical skills first');
  }
  
  const verifiedSkillsCount = skillBreakdown.filter(s => s.matched && s.verified).length;
  if (verifiedSkillsCount < matchedSkills.length / 2) {
    recommendations.push('Verify more of your skills to improve your profile strength');
  }
  
  return {
    matchScore,
    matchedSkills,
    missingCriticalSkills,
    skillBreakdown,
    recommendations
  };
}

/**
 * Filter tests based on skill matching
 */
export function filterTestsBySkillMatch(
  jobseekerSkills: JobseekerSkill[],
  availableTests: any[]
): any[] {
  if (!jobseekerSkills.length) {
    return availableTests; // Show all if no skills
  }

  return availableTests
    .map(test => {
      // First, try to use the test's primarySkills and tags directly (most accurate)
      // These are set by the AI when the test is generated
      const testPrimarySkills: string[] = test.primarySkills || [];
      const testTags: string[] = test.tags || [];

      // Combine primarySkills and tags for matching
      const directSkills = [...testPrimarySkills, ...testTags];

      let jobSkills: JobSkill[] = [];

      if (directSkills.length > 0) {
        // Convert direct skills to JobSkill format
        jobSkills = directSkills.map(skill => ({
          name: skill,
          required: testPrimarySkills.includes(skill), // Primary skills are required
          weight: testPrimarySkills.includes(skill) ? 7 : 4, // Primary skills weighted higher
        }));
      } else {
        // Fallback: Extract skills from job description or job title
        const jobDescription = test.jobDescription || test.jobTitle || '';
        jobSkills = extractSkillsFromJobDescription(jobDescription);
      }

      if (jobSkills.length === 0) {
        return { ...test, matchScore: 50 }; // Default score for tests without clear skill requirements
      }

      const matchResult = matchSkillsToJob(jobseekerSkills, jobSkills);

      return {
        ...test,
        matchScore: matchResult.matchScore,
        matchedSkills: matchResult.matchedSkills,
        missingSkills: matchResult.missingCriticalSkills,
        recommendations: matchResult.recommendations
      };
    })
    // Show ALL tests - don't filter any out, just sort by relevance
    // This ensures job seekers can see all available opportunities
    .sort((a, b) => {
      // Sort by match score (descending), then by creation date
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });
}

/**
 * Get skill recommendations for improving job matches
 */
export function getSkillRecommendations(
  jobseekerSkills: JobseekerSkill[],
  targetJobDescriptions: string[]
): string[] {
  const allJobSkills: JobSkill[] = [];
  
  targetJobDescriptions.forEach(desc => {
    const skills = extractSkillsFromJobDescription(desc);
    allJobSkills.push(...skills);
  });
  
  // Find most common missing skills
  const skillCounts = new Map<string, { count: number; weight: number }>();
  
  allJobSkills.forEach(jobSkill => {
    const hasSkill = jobseekerSkills.some(jsSkill => 
      calculateSkillSimilarity(jsSkill.name, jobSkill.name) > 0.7
    );
    
    if (!hasSkill && jobSkill.required) {
      const current = skillCounts.get(jobSkill.name) || { count: 0, weight: 0 };
      skillCounts.set(jobSkill.name, {
        count: current.count + 1,
        weight: current.weight + (jobSkill.weight || 5)
      });
    }
  });
  
  return Array.from(skillCounts.entries())
    .sort((a, b) => b[1].weight - a[1].weight)
    .slice(0, 5)
    .map(([skill]) => skill);
}